'use client'
import type { HttpClient, ApiConfig, FullRequestParams } from 'src/api/httpClient'

import { useRouter } from 'next/navigation'
import { useMemo } from 'react'

import { ROUTES, PUBLIC_ROUTES } from 'src/constants/routes'
import { TOKEN_KEY } from 'src/constants/session'
import createClient from 'src/api/createClient'

import useAppStorage from './useAppStorage'
import useLocale from './useLocale'

const getCurrentPath = () => {
  if (typeof window !== 'undefined') {
    return window.location.pathname + window.location.search + window.location.hash
  }
  return ''
}

type SecurityData = Record<string, string> | unknown | null
type RequestInterceptor = (requestParams: SecurityData) => Partial<SecurityData> | Promise<Partial<SecurityData>>
type ResponseInterceptor = (response: Response) => Response | Promise<Response>

type ApiHook = <T extends HttpClient>(
  ApiClient: new (config: ApiConfig) => T,
  extraConfig?: Partial<ApiConfig>,
  interceptors?: {
    requestInterceptor?: RequestInterceptor
    responseInterceptor?: ResponseInterceptor
  },
) => T

const defaultInterceptors = {
  requestInterceptor: async (param: SecurityData) => param,
  responseInterceptor: async (param: Response) => param,
}

// Module-level so the defaults are referentially stable. A `= {}` default parameter allocates a
// fresh object on every call, which would defeat the memo below for the ~30 call sites that pass
// no config at all.
const EMPTY_CONFIG: Partial<ApiConfig> = {}
const EMPTY_INTERCEPTORS = {}

const useApi: ApiHook = <T extends HttpClient>(
  ApiClient: new (config: ApiConfig) => T,
  extraConfig: Partial<ApiConfig> = EMPTY_CONFIG,
  interceptors = EMPTY_INTERCEPTORS,
): T => {
  const router = useRouter()
  const { getItem } = useAppStorage()
  const { locale } = useLocale()
  const {
    requestInterceptor,
    responseInterceptor,
  } = {
    ...defaultInterceptors,
    ...interceptors,
  }

  // The interceptors are real dependencies of the client, not incidental. Callers pass inline
  // arrows that close over values arriving later — `requestInterceptor: () => ({ headers: {
  // 'x-adstudio-token': adStudioToken } })` is the live example — and the compiler memoizes those
  // arrows on the values they capture. Depending on them means the client is rebuilt exactly when
  // the token changes, instead of being frozen with an empty one.

  /**
   * @description Security Worker
   * This function is used to handle security-related tasks such as adding authentication tokens to requests.
   * Or adding on the fly data outside the request implementation.
   * It can be customized to include additional security measures as needed.
   * @param securityData
   * @returns {Promise<Partial<FullRequestParams>>} - Returns a promise that resolves to a partial ApiConfig object.
   *
   */
  const apiClient = useMemo(() => {
    const { baseURL = process.env.NEXT_PUBLIC_API_URL, ...restConfig } = extraConfig

    // TODO: Move this to a config file or environment variable
    const defaultHeaders: Record<string, string> = {
      'x-platform-app': 'one',
    }
    if (locale) {
      defaultHeaders['Accept-Language'] = locale
    }

    const securityWorker = (securityData: SecurityData) => {
      const headers: Record<string, string> = {
        ...defaultHeaders,
      }

      // Read the token at request time rather than capturing it at render time.
      // Secure calls made imperatively right after login (e.g. the `/me` call in
      // the signup flow) run before this hook re-renders with the new session, so
      // a render-captured token would still be empty and the request would go out
      // without an Authorization header. `getItem` reads localStorage live, which
      // `loginWithToken` writes to synchronously before the call is made.
      const token = getItem(TOKEN_KEY)
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const interceptorResult = (requestInterceptor?.(securityData) || {}) as Partial<FullRequestParams>
      const requestParams: Partial<FullRequestParams> = {
        ...interceptorResult,
        headers: {
          ...headers,
          ...((interceptorResult.headers as Record<string, string>) ?? {}),
        },
      }

      return requestParams
    }

    /**
     * @description Default Response Interceptor
     * This function is used to handle responses from the API.
     * It can be customized to handle specific response codes or errors.
     * @param response - The response object from the API.
     * @returns {Promise<void>}
     */
    const securityHandler = async (response: Response): Promise<void> => {
      const currentPath = getCurrentPath()
      // handle 401 when made from a protected route using next/navigation
      if (response.status === 401 && !PUBLIC_ROUTES.some(route => currentPath.startsWith(route)) && !response.url?.endsWith('/login')) {
        console.warn('Unauthorized access detected, redirecting to login')
        router.push(ROUTES.LOGIN)
      }

      await responseInterceptor?.(response)
      // Do not return the response, just return void
    }

    const apiExtraConfig: ApiConfig = {
      baseURL,
      baseApiParams: { headers: defaultHeaders },
      ...restConfig,
      securityWorker,
      securityHandler,
    }
    return createClient<T>(ApiClient, apiExtraConfig as ApiConfig<unknown>)
  }, [ApiClient, extraConfig, locale, getItem, requestInterceptor, responseInterceptor, router])

  return apiClient
}

export default useApi
